import { Loader } from "@googlemaps/js-api-loader";
import { GeoSearch } from "./api/yext";
import geolocate from "../../utilities/geolocate";
import { asyncDebounce } from "../../utilities/debounce";

const MILE_IN_METERS = 1609.344;
const SHAPE_MARKER = [34, 7, 29.7, 7.4, 25.7, 8.6, 22.1, 10.6, 19, 13.2, 16.4, 16.3, 14.4, 19.9, 13.2, 23.9, 12.8, 28.2, 13.1, 32.1, 14.1, 35.7, 15.7, 39, 31, 56.1, 32, 56.6, 33, 56.9, 34, 57, 35, 56.9, 36, 56.6, 37, 56.1, 51.3, 40.6, 53.2, 37.4, 54.5, 33.9, 55.3, 28.2, 54.8, 23.9, 53.5, 19.9, 51.5, 16.3, 49, 13.2, 45.8, 10.6, 42.2, 8.6, 38.2, 7.4, 34, 7];
const SHAPE_MARKER_HIGHLIGHT = [46.9, 8.8, 38.7, 9.2, 33, 10.9, 28.2, 13.5, 24, 17, 20.5, 21.2, 16.9, 28.7, 15.7, 37.9, 16.1, 42, 18, 48.3, 20.3, 52.5, 38.8, 73.3, 40, 74.2, 41.3, 74.8, 42.7, 75.2, 44.1, 75.3, 45.5, 75.2, 47.5, 74.5, 48.7, 73.7, 67, 53.5, 69.9, 48.4, 71.9, 41.3, 72.3, 37.6, 71.8, 31.8, 70.3, 26.7, 68, 22.1, 64.9, 18, 61.2, 14.5, 54.5, 10.7, 46.9, 8.8];
const MAP_PADDING = {
	bottom: 100,
	left: 128,
	right: 128,
	top: 167,
};

function getUnitSystem() {
	return window?.theme?.localization?.country?.unitSystem || 'imperial';
}

function getLanguage() {
	return window?.theme?.localization?.language || 'en-US';
}

function getTime(time) {
	let [, hour, minute] = time.match(/(\d{2}):(\d{2})/);
	// AM or PM
	let period = +hour < 12 ? 'AM' : 'PM';
	// Convert from 24-hour to 12-hour clock.
	hour = (+hour % 12) || 12;

	return { hour, minute, period };
}

function getOpenIntervals(intervals = []) {
	return intervals?.reduce?.((interval, { start, end }) => {
		if (!start || !end) {
			return interval;
		}

		let s = getTime(start);
		let e = getTime(end);

		// Don't show start period if it is the same as the end period
		interval.push(`${s.hour}:${s.minute}${s.period === e.period ? '' : ` ${s.period}`} - ${e.hour}:${e.minute} ${e.period}`);

		return interval;
	}, [])?.join(', ') || '';
}

let GMaps = {
	loader: null,
	map: null,
	markers: [],
	queryMarker: null,
};

export default () => ({
	loading: false,
	querySuggestions: [],
	results: [],
	selectedIndex: -1,
	serviceOptions: [
		{ id: 'delivery', name: 'Delivery' },
		{ id: 'dinein', name: 'Dine-in' },
		{ id: 'takeout', name: 'Takeout' },
		{ id: 'drivethru', name: 'Drive-Thru' },
		{ id: 'curbsidepickup', name: 'Curbside Pickup' },
	],

	get filteredResults() {
		return this.results.filter(location => {
			// Filter out a location if it doesn't include one of the selected services
			for (let service of this.serviceFilters) {
				if (!location.serviceOptions.includes(service)) {
					return false;
				}
			}

			return true;
		})
	},

	get radiusResults() {
		return this.results.filter(location => location.distance <= this.radius);
	},

	get query() {
		return (sessionStorage.getItem('loc_query') ?? '');
	},
	set query(v) {
		sessionStorage.setItem('loc_query', v);
	},

	get queryLat() {
		return +(sessionStorage.getItem('loc_query_lat') ?? 0);
	},
	set queryLat(v) {
		sessionStorage.setItem('loc_query_lat', v);
	},

	get queryLng() {
		return +(sessionStorage.getItem('loc_query_lng') ?? 0);
	},
	set queryLng(v) {
		sessionStorage.setItem('loc_query_lng', v);
	},

	get radius() {
		return +(sessionStorage.getItem('loc_radius') ?? 10);
	},
	set radius(v) {
		sessionStorage.setItem('loc_radius', v);
		this.updateBounds();
	},

	get serviceFilters() {
		return JSON.parse(sessionStorage.getItem('loc_servicefilters') ?? '[]');
	},
	set serviceFilters(v) {
		sessionStorage.setItem('loc_servicefilters', JSON.stringify(v));
		this.updateMap();
	},

	get language() {
		return getLanguage();
	},
	get unitSystem() {
		return getUnitSystem();
	},

	get view() {
		return sessionStorage.getItem('loc_view') ?? 'search';
	},
	set view(v) {
		sessionStorage.setItem('loc_view', v);
	},

	init() {
		GMaps.loader = new Loader({
			apiKey: window?.theme?.integrations?.googleMaps?.apiKey || '',
			version: 'weekly',
			libraries: ['places'],
		});

		if (this.view === 'results') {
			if (this.query.trim()) {
				this.search();
			} else {
				this.view = 'search';
			}
		}

		this.$watch('selectedIndex', (newV, oldV) => {
			this.$nextTick(() => {
				if (oldV !== newV) {
					let results = this.$el.querySelectorAll('.location-result');

					if (oldV >= 0) {
						this.updateMarker(oldV);

						let details = results[oldV].querySelector('details');
						if (details) {
							details.open = false;
						}
					}

					if (newV >= 0) {
						this.updateMarker(newV);

						let details = results[newV].querySelector('details');
						if (details) {
							details.open = true;
						}

						results[newV].scrollIntoView({ block: 'center' });
					}
				}
			});
		});
	},

	geolocate() {
		// If geolocation is successfull, set lat/lng and search.
		const success = (position) => {
			const { latitude, longitude } = position.coords;

			this.queryLat = latitude;
			this.queryLng = longitude;

			this.query = 'My location';

			this.search();
		}

		geolocate(success)
	},

	fetch: asyncDebounce(async (params) => {
		let response = await GeoSearch.fetch(params);

		let results = {};

		if (response?.geo?.coordinate) {
			let { latitude: lat = 0, longitude: lng = 0 } = response.geo.coordinate;
			results.coordinate = { lat, lng };
		}

		results.locations = (response?.entities ?? []).map((entity, i) => {
			let {
				address: {
					city = '',
					line1 = '',
					line2 = '',
					postalCode = '',
					region = ''
				} = {},
				geomodifier = '',
				c_heroSection: {
					serviceOptions = [],
					orderCTA: {
						uRL: orderUrl = '',
					} = {},
				} = {},
				c_openMonth = '',
				hours = {},
				websiteUrl: {
					displayUrl: websiteUrl = ''
				} = {},
				yextDisplayCoordinate = {
					latitude: 0,
					longitude: 0
				},
			} = entity;

			let unitSystem = getUnitSystem();

			let location = {
				address: line2 ? `${line1}, ${line2} ${city}, ${region} ${postalCode}` : `${line1} ${city}, ${region} ${postalCode}`,
				distance: ((unitSystem === 'imperial' ? response?.distances?.[i]?.distanceMiles : response?.distances?.[i]?.distanceKilometers) || 0).toFixed(1),
				geomodifier: geomodifier || city,
				isComingSoon: c_openMonth ? new Date() < new Date(c_openMonth) : false,
				orderUrl,
				serviceOptions,
				websiteUrl,
				yextDisplayCoordinate,
				hours: {},
				holidayHours: {},
			};

			let { holidayHours = [], ...regularHours } = hours;

			location.hours = Object.entries(regularHours).reduce((acc, [day, { openIntervals = [], isClosed }]) => {
				if (!['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(day.toLowerCase())) {
					return acc;
				}

				acc[day] = (isClosed || !openIntervals.length) ? 'Closed' : getOpenIntervals(openIntervals);

				return acc;
			}, {});

			let language = getLanguage();
			location.holidayHours = holidayHours?.reduce((acc, { date, openIntervals = [], isClosed }) => {
				let dateString = new Date(date).toLocaleString(language, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

				acc[dateString] = (isClosed || !openIntervals.length) ? 'Closed' : getOpenIntervals(openIntervals);

				return acc;
			}, {});

			return location;
		})

		return results;
	}, 300, GeoSearch.abort),

	async search() {
		// Stop if there's no query
		if (!this.query.trim()) {
			return;
		}

		let query = this.query.toLowerCase() === 'my location' ? `${this.queryLat},${this.queryLng}` : this.query.trim();

		// Show loading indicator
		this.loading = true;

		// Compile params and filters
		let params = {
			entityTypes: 'restaurant',
			limit: 50,
			radius: 100,
			location: query,
		};

		const filters = [];
		// Differentiate Red Ribbon from Jollibee and Chowking
		filters.push({ name: { $contains: 'Red Ribbon', '!$contains': 'Closed' } })

		// Future-proofed should we need more filters
		params.filter = filters.length > 1 ? { $and: filters } : filters[0];

		// Limit response to fields we need
		const fields = [
			'address',
			'geomodifier',
			'yextDisplayCoordinate',
			'c_heroSection.serviceOptions',
			'c_heroSection.orderCTA.uRL',
			'c_openMonth',
			'hours',
			'websiteUrl.displayUrl',
		];
		params.fields = fields.join(',');

		// Send request
		let { coordinate, locations } = await this.fetch(params);

		// Update query coordinate if it exists
		if (coordinate) {
			let { lat, lng } = coordinate;

			this.queryLat = lat;
			this.queryLng = lng;
		}

		// Go to the results
		this.view = 'results';

		// Update results
		this.results = locations;

		if (this.results.length) {
			await this.updateMap();
		}

		// Hide loading indicator
		this.loading = false;
	},

	// If the selected index is already the current one, close it. Otherwise open it.
	toggle(i) {
		this.selectedIndex = (this.selectedIndex === i ? -1 : i);
	},

	// GOOGLE MAPS
	// ==================================================
	async loadGMaps() {
		return GMaps.loader.load();
	},

	// Autocomplete
	// --------------------------------------------------
	async initAutocompleteWidget($el) {
		await this.loadGMaps();

		let autocomplete = new google.maps.places.Autocomplete($el, {
			componentRestrictions: {
				country: ['us', 'ca']
			},
			// Don't get business results
			types: ['geocode'],
			// Get the autocomplete place's full address
			fields: ['formatted_address'],
		});

		autocomplete.addListener('place_changed', () => {
			let { formatted_address, name } = autocomplete.getPlace();
			this.query = formatted_address || name;

			this.search();
		})
	},

	// Map
	// --------------------------------------------------
	async getMap() {
		// Initialize map if it doesn't exist
		if (GMaps.map === null) {
			await this.loadGMaps();

			GMaps.map = new google.maps.Map(this.$refs.map, {
				// Disable controls
				fullscreenControl: false,
				mapTypeControl: false,
				streetViewControl: false,
				// Set max zoom
				maxZoom: 15,
				// Remove distractions
				clickableIcons: false,
				styles: [
					{
						featureType: 'poi.business',
						elementType: 'labels',
						stylers: [{ visibility: 'off' }]
					}
				],
			});
		}

		return GMaps.map;
	},

	async updateMap() {
		await this.updateQueryMarker();
		await this.updateMarkers();
		await this.updateRestrictions();
		await this.updateBounds();

		return true;
	},

	async updateQueryMarker() {
		let map = await this.getMap();

		// Do nothing if map doesn't exist
		if (!map) {
			return;
		}

		// Create marker if it doesn't already exist.
		if (GMaps.queryMarker === null) {
			GMaps.queryMarker = new google.maps.Marker({
				map,
				icon: {
					url: window?.theme?.integrations?.googleMaps?.markers?.yourLocation || '',
					anchor: new google.maps.Point(23, 23),
					scaledSize: new google.maps.Size(46, 46),
					zIndex: 1,
				},
				title: 'You are here',
			})
		}

		// If there's lat/lng, update position, or remove it from the map.
		if (this.queryLat && this.queryLng) {
			GMaps.queryMarker.setMap(map);
			GMaps.queryMarker.setPosition({ lat: this.queryLat, lng: this.queryLng });
		} else {
			GMaps.queryMarker.setMap(null);
		}

		return true;
	},

	async updateMarker(index) {
		let map = await this.getMap();

		if (!map) {
			return;
		}

		let marker = GMaps.markers[index];
		let highlight = this.selectedIndex === index;
		let { isComingSoon = false } = this.filteredResults?.[index];

		marker.setOptions({
			icon: {
				url: window?.theme?.integrations?.googleMaps?.markers?.[isComingSoon ? 'comingSoon' : 'location']?.[highlight ? 'highlight' : 'default'],
				anchor: highlight ? new google.maps.Point(44, 75.4) : new google.maps.Point(34, 57),
				scaledSize: highlight ? new google.maps.Size(88, 88) : new google.maps.Size(68, 68),
			},
			shape: {
				coords: highlight ? SHAPE_MARKER_HIGHLIGHT : SHAPE_MARKER,
				type: 'poly',
			},
			zIndex: highlight ? 2 : 1,
		});

		// Pan to include selected marker if it isn't visible on the map.
		if (highlight && GMaps.map.getBounds()?.contains?.(marker.getPosition()) === false) {
			let bounds = new google.maps.LatLngBounds();
			bounds.extend(marker.getPosition());

			// Pad the bounds to make sure the marker shows up along with the coordinate.
			GMaps.map.panToBounds(bounds, MAP_PADDING)
		}
	},

	async updateMarkers() {
		let map = await this.getMap();

		// Do nothing if map doesn't exist
		if (!map) {
			return;
		}

		// Clear location markers
		GMaps.markers.forEach(marker => {
			marker.setMap(null)
		});
		GMaps.markers = [];

		// Add location markers
		this.filteredResults.forEach((location, i) => {
			let {
				geomodifier = '',
				isComingSoon = false,
				yextDisplayCoordinate: {
					latitude: lat = 0,
					longitude: lng = 0
				} = {},
			} = location;

			// Skip if lat/lng don't exist or are 0
			if (!lat || !lng) {
				return;
			}

			let marker = new google.maps.Marker({
				map,
				position: { lat, lng },
				icon: {
					url: window?.theme?.integrations?.googleMaps?.markers?.[isComingSoon ? 'comingSoon' : 'location']?.default,
					anchor: new google.maps.Point(34, 57),
					scaledSize: new google.maps.Size(68, 68),
					zIndex: 1,
				},
				shape: {
					coords: SHAPE_MARKER,
					type: 'poly',
				},
				title: geomodifier,
			});

			marker.addListener('click', () => {
				this.toggle(i);
			})

			GMaps.markers.push(marker);
		});

		return true;
	},

	async updateRestrictions() {
		let map = await this.getMap();

		// Do nothing if map doesn't exist
		if (!map) {
			return;
		}

		// Restrict panning/zooming
		let circle = new google.maps.Circle({
			radius: 100 * MILE_IN_METERS,
			center: {
				lat: this.queryLat,
				lng: this.queryLng
			}
		});

		this.$nextTick(() => {
			GMaps.map.setOptions({
				restriction: {
					latLngBounds: circle.getBounds(),
				},
			})
		});

		return true;
	},

	async updateBounds() {
		let map = await this.getMap();

		// Do nothing if map doesn't exist
		if (!map) {
			return;
		}

		let bounds = new google.maps.LatLngBounds();

		// Extend to fit query location if it exists
		if (this.queryLat && this.queryLng) {
			bounds.extend({ lat: this.queryLat, lng: this.queryLng });
		}

		// Extend to fit locations on the map
		this.filteredResults.forEach((location) => {
			let {
				distance = 0,
				yextDisplayCoordinate: {
					latitude: lat = 0,
					longitude: lng = 0
				} = {}
			} = location;

			// Skip if lat/lng don't exist or are 0
			if (!lat || !lng) {
				return;
			}

			// Extend bounds for locations within the selected radius
			if (distance <= this.radius) {
				bounds.extend({ lat, lng });
			}
		})

		// If no locations within radius, extend to fit selected radius
		if (!this.radiusResults.length) {
			let circle = new google.maps.Circle({
				radius: this.radius * (this.unitSystem === 'imperial' ? MILE_IN_METERS : 1000),
				center: {
					lat: this.queryLat,
					lng: this.queryLng
				}
			});

			bounds.union(circle.getBounds());
		}

		this.$nextTick(() => {
			GMaps.map.fitBounds(bounds, MAP_PADDING);
		})

		return true;
	},

})
